import {
  AccountCircle,
  Logout,
  Menu as MenuIcon,
  Settings,
} from "@mui/icons-material";
import {
  AppBar,
  Avatar,
  Box,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
} from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { logoutUser } from "../store/slices/authSlice";
import {
  getAvatarColor,
  getAvatarImageSrc,
  getAvatarInitials,
  hasProfilePicture,
} from "../utils/avatarUtils";
import AppLogo from "./AppLogo.jsx";
import LanguageSwitcher from "./LanguageSwitcher.jsx";
import ThemeSwitcher from "./ThemeSwitcher.jsx";

const Navbar = ({ onMenuClick, isSidebarOpen, onLogout, onNavigate }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const [anchorEl, setAnchorEl] = React.useState(null);

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    if (onLogout) {
      onLogout();
    }
    dispatch(logoutUser());
  };

  const isMenuOpen = Boolean(anchorEl);

  const menuId = "primary-search-account-menu";
  const renderMenu = (
    <Menu
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      id={menuId}
      keepMounted
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      open={isMenuOpen}
      onClose={handleMenuClose}
    >
      <MenuItem
        onClick={() => {
          handleMenuClose();
          if (onNavigate) {
            onNavigate("/profile");
          }
        }}
      >
        <AccountCircle sx={{ mr: 1 }} />
        {t("navbar.profile")}
      </MenuItem>
      <MenuItem
        onClick={() => {
          handleMenuClose();
          if (onNavigate) {
            onNavigate("/settings");
          }
        }}
      >
        <Settings sx={{ mr: 1 }} />
        {t("navbar.settings")}
      </MenuItem>
      <Divider />
      <MenuItem onClick={handleLogout}>
        <Logout sx={{ mr: 1 }} />
        {t("navbar.logout")}
      </MenuItem>
    </Menu>
  );

  if (!isAuthenticated) {
    return null;
  }

  return (
    <AppBar
      position="fixed"
      sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
    >
      <Toolbar>
        <IconButton
          color="inherit"
          aria-label="open drawer"
          onClick={onMenuClick}
          edge="start"
          sx={{ mr: 2 }}
        >
          <MenuIcon />
        </IconButton>

        <Box
          sx={{
            flexGrow: 1,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <AppLogo height={40} />
          <Typography variant="h6" noWrap component="div" sx={{ fontWeight: "bold" }}>
            {t("navbar.title")}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center" }}>
          <ThemeSwitcher />
          <LanguageSwitcher />
          <Typography
            variant="body1"
            noWrap
            sx={{
              mr: 2,
              maxWidth: { sm: 140, md: 220 },
              display: { xs: "none", sm: "block" },
            }}
          >
            {user?.username || user?.name || t("common.user")}
          </Typography>
          <IconButton
            size="large"
            edge="end"
            aria-label="account of current user"
            aria-controls={menuId}
            aria-haspopup="true"
            onClick={handleProfileMenuOpen}
            color="inherit"
          >
            <Avatar
              src={getAvatarImageSrc(user)}
              sx={{
                width: 40,
                height: 40,
                fontSize: "1rem",
                bgcolor: hasProfilePicture(user)
                  ? undefined
                  : getAvatarColor(user),
              }}
            >
              {hasProfilePicture(user) ? undefined : getAvatarInitials(user)}
            </Avatar>
          </IconButton>
        </Box>
      </Toolbar>
      {renderMenu}
    </AppBar>
  );
};

export default Navbar;
